import type { Task, TaskPriority, TaskSummary } from './types'

/**
 * The one durable task this session is currently working toward (RFC 0304 3a).
 *
 * A pending task can be a follow-up the session merely recorded, so it cannot
 * become the goal just because it was written most recently. Taking work is the
 * explicit `pending -> active` transition; among active tasks, the most recently
 * touched one is the session's current goal. The goal is therefore session
 * state projected from its task refs, not another field stored on the task.
 *
 * Only work this session TOOK counts. Creating a task and handing it to another
 * session leaves a created-by ref here, and treating that as a goal pushed both
 * sessions to continue the same task.
 */
export function sessionGoalTask(
  tasks: readonly TaskSummary[],
  sessionId: string,
): TaskSummary | null {
  const worksIt = (task: TaskSummary): boolean =>
    task.refs.some(
      (ref) =>
        ref.type === 'session' &&
        ref.target === sessionId &&
        ref.role === 'worked-by',
    )
  const byId = new Map(tasks.map((task) => [task.id, task]))
  const hasActiveAncestor = (task: TaskSummary): boolean => {
    const seen = new Set<string>([task.id])
    let parentId = task.parentId
    while (parentId && !seen.has(parentId)) {
      seen.add(parentId)
      const parent = byId.get(parentId)
      if (!parent) return false
      if (parent.status === 'active') return true
      parentId = parent.parentId
    }
    return false
  }
  return (
    tasks
      .filter(
        (task) =>
          task.status === 'active' && worksIt(task) && !hasActiveAncestor(task),
      )
      .sort(
        (a, b) =>
          b.updatedAt.localeCompare(a.updatedAt) || b.id.localeCompare(a.id),
      )[0] ?? null
  )
}

const PRIORITY_RANK: Record<TaskPriority, number> = {
  critical: 4,
  high: 3,
  normal: 2,
  low: 1,
}

/**
 * The task a finishing session should put in front of the person (RFC 0304
 * 3c). Pending is the ownership boundary: active means another session has
 * already taken it, while blocked is not startable. Related work wins before
 * global priority, then older work wins so a busy project does not starve.
 */
export function nextUnownedTask(
  completed: Pick<Task, 'id' | 'parentId' | 'refs'>,
  tasks: readonly TaskSummary[],
): TaskSummary | null {
  const relatedRefs = new Set(
    (completed.refs ?? [])
      .filter((ref) => ref.role === 'about')
      .map((ref) => `${ref.type}:${ref.target}`),
  )
  const relation = (task: TaskSummary): number => {
    if (task.parentId === completed.id) return 3
    if (completed.parentId && task.parentId === completed.parentId) return 2
    if (
      (task.refs ?? []).some(
        (ref) =>
          ref.role === 'about' && relatedRefs.has(`${ref.type}:${ref.target}`),
      )
    )
      return 1
    return 0
  }
  return (
    tasks
      .filter((task) => task.status === 'pending' && task.id !== completed.id)
      .sort(
        (a, b) =>
          relation(b) - relation(a) ||
          PRIORITY_RANK[b.priority ?? 'normal'] -
            PRIORITY_RANK[a.priority ?? 'normal'] ||
          a.updatedAt.localeCompare(b.updatedAt) ||
          a.id.localeCompare(b.id),
      )[0] ?? null
  )
}
